// src/components/chat/chatHelpers.js
// Pure utility functions extracted from ChatWorld.jsx.
// No React, no stores — just plain JS helpers.

/** Real user mxid (e.g. @u109:domain), never admin/bot */
export const isRealUserMxid = (id) => /^@u\d+:/i.test(String(id || ''));

/** Admin mxid check */
export const isAdminMxid = (id) => /^@?admin(?::|$)/i.test(String(id || ''));

/** Extract eHandshake u_id from Matrix ID (e.g. @u830:ehandshake.com → 830) */
export function extractUidFromMxid(mxid) {
  const m = String(mxid || '').match(/^@u(\d+):/);
  return m ? Number(m[1]) : null;
}

/** Initials for fallback avatar */
export function initialsOf(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Compact relative timestamp for chat list rows */
export function tinyWhen(ts) {
  const t = Number(ts || 0);
  if (!t) return '';
  const d = new Date(t);
  const now = new Date();

  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === y.getFullYear() &&
    d.getMonth() === y.getMonth() &&
    d.getDate() === y.getDate();
  if (isYesterday) return 'Yesterday';

  const opts = { month: 'numeric', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) Object.assign(opts, { year: '2-digit' });
  return d.toLocaleDateString([], opts);
}

/**
 * Return the best timestamp for an invite:
 * 1) my own invite event (if I'm the invitee)
 * 2) the peer's invite event (if I'm the inviter)
 * 3) room create event (last-resort)
 */
export function inviteTimestamp(room, myMxid) {
  try {
    const me = room.getMember?.(myMxid);
    const meInviteTs =
      me?.membership === 'invite'
        ? (me?.events?.member?.getTs?.() ??
           me?.events?.member?.event?.origin_server_ts ??
           0)
        : 0;
    if (meInviteTs) return meInviteTs;

    const others = room.getMembers?.() || [];
    const peer = others.find(m => m?.userId && m.userId !== myMxid && m.membership === 'invite');
    const peerInviteTs =
      peer?.events?.member?.getTs?.() ??
      peer?.events?.member?.event?.origin_server_ts ??
      0;
    if (peerInviteTs) return peerInviteTs;

    const ev = room?.currentState?.getStateEvents?.('m.room.create', '');
    const createTs = ev?.getTs?.() ?? ev?.event?.origin_server_ts ?? 0;
    return createTs || 0;
  } catch {
    return 0;
  }
}

/** Canonical 1:1 DM alias regex: #eh.dm.u<min>_u<max>:domain */
export const DM_ALIAS_RE = /^#eh\.dm\.u(\d+)_u(\d+):/i;

/** Extract UID from mxid (alternative compact form) */
export function uidFromMxid(mxid) {
  const m = String(mxid || '').match(/^@u(\d+):/i);
  return m ? Number(m[1]) : null;
}

/** Get peer's eHandshake u_id from a DM room (alias-first, then members fallback) */
export function peerUidFromRoom(room, myMxid) {
  const alias = room.getCanonicalAlias?.() || (room.getAliases?.() || [])[0] || '';
  const am = String(alias).match(DM_ALIAS_RE);
  if (am) {
    const a = Number(am[1]), b = Number(am[2]);
    const me = uidFromMxid(myMxid);
    if (Number.isFinite(me)) {
      return (me === a) ? b : (me === b ? a : null);
    }
    return a;
  }

  try {
    const list = room.getMembers?.() || [];
    const other = list.find(m => m?.userId && m.userId !== myMxid && isRealUserMxid(m.userId));
    return other ? uidFromMxid(other.userId) : null;
  } catch { return null; }
}

/** Convert peer's mxc avatar → http(s) img URL */
export function peerAvatarHttp(room, mxClient, myUserId) {
  try {
    const list = room.getMembers?.() || [];
    const peer =
      list.find(m => m?.userId && m.userId !== myUserId && isRealUserMxid(m.userId)) || null;
    if (!peer) return null;

    const mxc = peer.getMxcAvatarUrl?.();
    if (mxc && mxClient?.mxcUrlToHttp) {
      const httpUrl = mxClient.mxcUrlToHttp(mxc);
      return httpUrl;
    }
  } catch (err) {
    console.warn('[chatHelpers] peerAvatarHttp error', err);
  }
  return null;
}

/** Other participant name (prefer real user, ignore admin) */
export function roomDisplay(room, myUserId) {
  try {
    const joined = room.getJoinedMembers?.() || [];
    const peer =
      joined.find(m => m?.userId !== myUserId && isRealUserMxid(m.userId)) ||
      (room.getMembers?.() || []).find(m => m?.userId !== myUserId && isRealUserMxid(m.userId)) ||
      null;
    if (peer?.name) return peer.name;
    if (peer?.userId) return peer.userId;
  } catch {}
  return room.getCanonicalAlias?.() || room.roomId;
}

/** Strict 1:1 DM check — exactly two real users, no admin */
export function isStrictDM(room) {
  try {
    const canonical =
      room.getCanonicalAlias?.() ||
      ((room.getAliases?.() || [])[0] || null);

    if (canonical && String(canonical).startsWith('#t')) {
      return false;
    }

    const all = room.getMembers?.() || [];
    const ids = all
      .filter(m => /^(join|invite)$/i.test(String(m?.membership || '')))
      .map(m => m?.userId)
      .filter(id => id && isRealUserMxid(id) && !isAdminMxid(id));

    const uniq = Array.from(new Set(ids));
    return uniq.length === 2;
  } catch { return false; }
}

/** Last text preview + ts from room timeline */
export function lastText(room) {
  try {
    const events =
      (room.getLiveTimeline?.().getEvents?.() || []) ||
      room.timeline || [];

    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if (ev?.getType?.() !== 'm.room.message') continue;

      const c = ev.getContent?.() || {};
      if (c.msgtype !== 'm.text') continue;

      let body = (c.body || '').toString().trim();
      if (!body) continue;

      // Strip Matrix reply fallback ("> <@user:domain> quoted text\n\nactual reply")
      if (body.startsWith('> ')) {
        const idx = body.indexOf('\n\n');
        if (idx >= 0) body = body.slice(idx + 2);
      }

      const ts = Number(ev.getTs?.() || 0) || 0;

      if (body.startsWith('__EH_TEAM_INVITE__::')) {
        const raw = body.slice('__EH_TEAM_INVITE__::'.length);
        try {
          const payload = JSON.parse(raw);
          const name =
            payload?.team_name && String(payload.team_name).trim()
              ? String(payload.team_name).trim()
              : (payload?.team_id != null ? `Team #${payload.team_id}` : 'Team');

          return {
            text: `Team ${name} invitation has been sent`,
            ts,
            tone: 'teamInvite',
          };
        } catch {
          return { text: 'Team invitation has been sent', ts, tone: 'teamInvite' };
        }
      }

      return { text: body, ts, tone: 'normal' };
    }
  } catch {}

  return { text: null, ts: 0, tone: 'normal' };
}

/** Format ISO-like date to "MM/DD/YYYY  HH:MM am/pm" */
export function formatFullDateTime(isoLike) {
  if (!isoLike) return '';
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return '';
  const dateStr = d.toLocaleDateString([], {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timeStr = d
    .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    .toLowerCase();
  return `${dateStr}  ${timeStr}`;
}

/** Calculate days remaining on invite cooldown */
export function calculateCooldownDaysRemaining(invitedAtIso, days = 30) {
  if (!invitedAtIso) return 0;
  const invitedMs = Date.parse(invitedAtIso);
  if (Number.isNaN(invitedMs)) return 0;

  const cooldownMs = days * 24 * 60 * 60 * 1000;
  const endMs = invitedMs + cooldownMs;
  const diffMs = endMs - Date.now();

  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

/** Build display message for terminal team invite status (accepted/declined/expired) */
export function buildInviteStatusMessage(data) {
  const formatLongDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
  };

  const reason = String(data?.terminal_reason || '').toLowerCase();

  if (reason.includes('accepted')) {
    const when = formatLongDate(data?.responded_at || data?.accepted_at);
    return when
      ? `You accepted this invite on ${when}.`
      : 'You have already accepted this team invitation.';
  }

  if (reason.includes('declined')) {
    const when = formatLongDate(data?.responded_at || data?.declined_at);
    return when
      ? `You declined this invite on ${when}.`
      : 'You have already declined this team invitation.';
  }

  if (reason.includes('expired')) {
    const when = formatLongDate(data?.expires_at || data?.expired_at);
    return when
      ? `This team invitation expired on ${when}. Contact the team captain to receive a new invitation.`
      : 'This team invitation has expired. Contact the team captain to receive a new invitation.';
  }

  return 'You have already responded to this team invitation.';
}
